import React, { useEffect, useMemo, useState } from "react";
import useResponsiveFlags from "../../hooks/useResponsiveFlags";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import axios from "../../api/axios";
import FilterMenuAgreement from "./FilterMenuAgreement";
import AgreementHeadFiles from "./AgreementTableHead";
import {  IconButton, Button, Alert } from "@mui/material";
import WrapperContainer from "../../components/utilities/WrapperContainer";

import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import AgreementViewer from "../../components/AgreementView";
import Box from "@mui/material/Box";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import download from "../../assests/images/download.svg";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CachedTwoToneIcon from '@mui/icons-material/CachedTwoTone';

const AgreementTable = () => {
  const { isDesktop, isSmallMobileScreen } = useResponsiveFlags();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("org");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [filters, setFilters] = useState({
    agreement_type: null,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [agreementViewerOpen, setAgreeementViewerOpen] = useState(false);
  const [pdfAgreementData, setPdfAgreementData] = useState("");

  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    orgid: "",
    agreement_type: "",
  });
  const [organizations, setOrganizations] = useState([]);
  const [uploadFormErrorMessage, setUploadFormErrorMessage] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters, page, rowsPerPage]);

  useEffect(() => {
    if (!search) handleSearchButtonClick();
  }, [search]);

  const handleOpenUploadDialog = () => {
    if (organizations && organizations.length > 0) {
      setUploadDialog(true);
      setUploadFormErrorMessage("");
      setUploadForm({
        orgid: "",
        agreement_type: "",
      });
      setUploadFile(null);
    } else {
      setErrorMessage("No Organizations Data available.");
    }
  };

  const handleCloseUploadDialog = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    setUploadDialog(false);
    setUploadFormErrorMessage("");
    setUploadForm({
      orgid: "",
      agreement_type: "",
    });
    setUploadFile(null);
  };

  const handleUploadFormChange = (field, value) => {
    setUploadForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const maxSize = 2 * 1024 * 1024; //2 mb

    if (!file) return;

    if (file.type !== "application/pdf") {
      setUploadFormErrorMessage("Please select a valid PDF file.");
      setTimeout(() => setUploadFormErrorMessage(""), 3000);
      event.target.value = "";
      return;
    }
    if (file.size > maxSize) {
      setUploadFormErrorMessage("File size must be less then 2 MB");
      setTimeout(() => setUploadFormErrorMessage(""), 3000);
      event.target.value = "";
      return;
    }
    setUploadFormErrorMessage("");
    setUploadFile(file);
  };

  const fetchOrganizations = async () => {
    axios
      .get("/api/admin_dashboard/getagreementorgs")
      .then((response) => {
        setOrganizations(response.data || []);
      })
      .catch((err) => {
        setErrorMessage("Failed to fetch organizations for upload agreement form.");
      });
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) {
      setUploadFormErrorMessage("Please select a file to upload");
      setTimeout(() => setUploadFormErrorMessage(""), 3000);
      return;
    }

    if (!uploadForm.orgid || !uploadForm.agreement_type) {
      setUploadFormErrorMessage("Please fill all required fields");
      setTimeout(() => setUploadFormErrorMessage(""), 3000);
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("document", uploadFile);
    formData.append("orgid", uploadForm.orgid);
    formData.append("agreementtype", uploadForm.agreement_type);
    axios.post('/api/admin_dashboard/agreementupload', formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    }).then((response) => {
      if (response.data.success) {
        setSuccessMessage("Agreement uploaded successfully");
        setTimeout(() => setSuccessMessage(""), 3000);
        handleCloseUploadDialog();
        fetchData();
      } else {
        if (response.data.message) {
          setUploadFormErrorMessage(response.data.message);
        } else {
          setUploadFormErrorMessage("Error while processing the uploaded agreement. Agreement upload failed.");
        }
      }
      setUploading(false);
    }).catch((err) => {
      setUploadFormErrorMessage("Error while uploading the agreement document.");
      setUploading(false);
    });
  }

  const fetchData = async () => {
    setDataLoading(true);
    setErrorMessage("");
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
    };
    if (order) params.sort_order = order;
    if (orderBy) params.sort_by = orderBy;
    if (filters.agreement_type) params.agreement_type = filters.agreement_type;
    if (search) params.search = search;
    axios
      .get("/api/admin_dashboard/agreementsdata", { params })
      .then((response) => {
        const result = response.data;
        setData(result.data || []);
        setTotalCount(result.total || 0);
        setDataLoading(false);
      })
      .catch((err) => {
        setErrorMessage("Failed to fetch agreement table data.");
        setDataLoading(false);
      });
  };

  const handleDocuSignGenerationErrorResponse = (data) => {
    if (data.status === "Failed") {
      setErrorMessage(data.message);
      return;
    }

    if (data.status === "AuthRequired") {
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setErrorMessage(data.message);
      }
    }
  }
  const handleMarkTokenGenerated = async(agreement_id,type) =>{
    setLoading(true);
    setErrorMessage("");
    await axios.post('/api/admin_dashboard/updateagreementstatus',{
      'id': agreement_id,
      'type': type,
    }).then((res)=>{
      if (res.data?.success){
        setSuccessMessage(res.data.message);
        fetchData();
      } else {
        if (res.data.message)
          setErrorMessage(res.data.message);
        else 
          setErrorMessage("Faied to update the agreement status with agreement Id : ". agreement_id);
      }
      setLoading(false);
    }).catch((res)=>{
      setErrorMessage("Faied to update the agreement status with agreement Id : ". agreement_id);
      setLoading(false);
    })
  }
  const fetchAgreement = async (agreement_id, type, action) => {
    setLoading(true);
    setErrorMessage("");
    await axios.post('/api/admin_dashboard/view-agreement-pdf', {
      'id': agreement_id,
      'type': type,
    }).then((res) => {
      if (res.data?.success) {
        setPdfAgreementData({
          pdfBase64: res.data.pdfBase64,
          orgName: res.data.orgName,
          agreementType: res.data.agreementType,
        });
        if (action == "view") {
          setAgreeementViewerOpen(true);
        }
        if (action == "download") {
          handleDownloadAgreement(res.data.pdfBase64, res.data.orgName, res.data.agreementType);
        }
      } else {
        if (res.data?.status) {
          handleDocuSignGenerationErrorResponse(res.data);
        } else if (res.data.message) {
          setErrorMessage(res.data.message);
        } else {
          setErrorMessage("Faied to fetch agreement PDF");
        }
      }
      setLoading(false);
    }).catch((res) => {
      setErrorMessage("Faied to fetch agreement PDF");
      setLoading(false);
    })
  }

  const handleDownloadAgreement = async (pdfBase64, orgName, agreementType) => {
    if (!pdfBase64) return;
    const fileName = `${orgName}_${agreementType}.pdf`;
    const link = document.createElement("a");
    link.href = `data:application/pdf;base64,${pdfBase64}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: prevFilters[column] === value ? null : value,
    }));
    setPage(0);
  };
  const handleSearchChange = (event) => {
    setSearch(event.target.value.toLowerCase());
  };

  const handleSearchButtonClick = () => {
    if (page != 0) setPage(0);
    else fetchData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return [...data].sort(getComparator(order, orderBy));
  }, [data, order, orderBy]);

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (orderBy === "year") {
      valueA = Number(valueA) || 0;
      valueB = Number(valueB) || 0;
    }

    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (valueB < valueA) return -1;
    if (valueB > valueA) return 1;
    return 0;
  }
  const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
    },
  }));

  return (
    <>
      <WrapperContainer
        title="Agreements"
        loggedin={true}
        content={
          <>
            <div className="d-flex flex-column-reverse flex-xl-row justify-content-between mb-xl-3">
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  className="form-control search-icon"
                  placeholder="Search"
                  value={search}
                  onChange={handleSearchChange}
                />
                <Button
                  size="medium"
                  variant="contained"
                  onClick={handleSearchButtonClick}
                  sx={{ padding: "6px 0", marginLeft: "10px" }}
                >
                  Search
                </Button>
                <FilterMenuAgreement
                  handleFilterChange={handleFilterChange}
                  filters={filters}
                />
              </div>
              <div className="mb-3 mb-xl-0">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenUploadDialog}
                >
                  Upload Agreement
                </Button>
              </div>
            </div>

            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage("")}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert
                severity="success"
                onClose={() => setSuccessMessage("")}
                sx={{ mb: 2 }}
              >
                {successMessage}
              </Alert>
            )}
            <AgreementViewer
              open={agreementViewerOpen}
              onClose={() => { setAgreeementViewerOpen(false); setPdfAgreementData(""); }}
              pdfBase64={pdfAgreementData?.pdfBase64}
            />
            {loading && <ShowLoading />}
            <TableContainer
              className="table-scroll-visible"
              sx={{
                height: isDesktop
                  ? "calc(100vh - 227px)"
                  : "calc(100vh - 297px)",
                minHeight: isDesktop ? "500px" : "300px",
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <Table stickyHeader sx={{ minWidth: 750 }} aria-label="agreement table">
                <AgreementHeadFiles
                  order={order}
                  orderBy={orderBy}
                  onSort={handleSort}
                />
                <TableBody>
                  {dataLoading ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedData.length > 0 ? (
                    sortedData.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell
                          style={
                            !isSmallMobileScreen
                              ? {
                                position: "sticky",
                                top: 0,
                                left: 0,
                                zIndex: 10,
                                background: "#fff",
                              }
                              : {}
                          }
                          className="custom-size"
                        >
                          {row.org}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.agreement_type}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.user}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.type}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.year}
                        </TableCell>
                        <TableCell className="custom-size">
                          <Box display="flex" alignItems="center" gap={1}>
                            {row.status}
                            {row.toggleActionShow && (
                              <BootstrapTooltip title="Mark as Token Generated">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkTokenGenerated(row.id, row.type)}
                                  sx={{
                                    backgroundColor: "#2D76D2",
                                    color: "#fff",
                                    "&:hover": {
                                      backgroundColor: "#1e5bb8",
                                    },
                                    width: 26,
                                    height: 26,
                                  }}
                                >
                                  <CachedTwoToneIcon fontSize="medium" />
                                </IconButton>
                              </BootstrapTooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.signed}
                        </TableCell>
                        <TableCell className="custom-size">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            gap={1}
                          >
                            <BootstrapTooltip title="View Agreement">
                              <span>
                                <button
                                  className="results-btn btn btn-sm"
                                  onClick={() => fetchAgreement(row.id, row.type, "view")}
                                  disabled={
                                    row.actionDisabled
                                  }
                                  style={{
                                    opacity: row.actionDisabled ? 0.5 : 1,
                                    cursor: row.actionDisabled
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                ><VisibilityIcon />
                                </button>
                              </span>
                            </BootstrapTooltip>
                            <BootstrapTooltip title="Download">
                              <span>
                                <button
                                  className="results-btn btn btn-sm"
                                  onClick={() => fetchAgreement(row.id, row.type, "download")}
                                  disabled={
                                    row.actionDisabled
                                  }
                                  style={{
                                    opacity: row.actionDisabled ? 0.5 : 1,
                                    cursor: row.actionDisabled
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                >
                                  <img src={download} alt="Download" />
                                </button>
                              </span>
                            </BootstrapTooltip>
                          </Box>
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.agreement_id}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        {search
                          ? "No matching agreement found."
                          : "No agreements found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!dataLoading && totalCount > 0 && (
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count} total agreements`
                }
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    alignItems: "center",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    margin: 0,
                  },
                  "& .MuiTablePagination-displayedRows": {
                    margin: 0,
                  },
                }}
              />
            )}
            <Dialog
              open={uploadDialog}
              onClose={handleCloseUploadDialog}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Upload Agreement</DialogTitle>
              <DialogContent>
                {uploadFormErrorMessage && (
                  <Alert
                    severity="error"
                    onClose={() => setUploadFormErrorMessage("")}
                    sx={{}}
                  >
                    {uploadFormErrorMessage}
                  </Alert>
                )}
                <div style={{ padding: "0" }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Organization*</InputLabel>
                    <Select
                      label="Organization"
                      value={uploadForm.orgid}
                      onChange={(e) =>
                        handleUploadFormChange("orgid", e.target.value)
                      }
                      required
                    >
                      {organizations && organizations.length > 0 ? (organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.id} - {org.name}
                        </MenuItem>
                      ))) : <MenuItem disabled>No Organizations Found</MenuItem>}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Agreement Type*</InputLabel>
                    <Select
                      label="Agreement Type"
                      value={uploadForm.agreement_type}
                      onChange={(e) =>
                        handleUploadFormChange("agreement_type", e.target.value)
                      }
                      required
                    >
                      <MenuItem value="Fractions_FreeTrialAgreement">
                        iSort Fractions - Free Trial Agreement
                      </MenuItem>
                      <MenuItem value="Fractions_PerSampleToken_Agreement">
                        iSort Fractions - Per Sample Agreement
                      </MenuItem>
                      <MenuItem value="Fractions_SeatTokenAgreement">
                        iSort Fractions - Seat Based Agreement
                      </MenuItem>
                      <MenuItem value="HiRes_FreeTrialAgreement">
                        iSort HiRes - Free Trial Agreement
                      </MenuItem>
                      <MenuItem value="HiRes_PerSampleToken_Agreement">
                        iSort HiRes - Per Sample Agreement
                      </MenuItem>
                      <MenuItem value="HiRes_SeatTokenAgreement">
                        iSort HiRes - Seat Based Agreement
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <div style={{ marginTop: "20px" }}>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      style={{ display: "block", marginBottom: "10px" }}
                    />
                    {uploadFile && (
                      <p style={{ color: "green", fontSize: "14px" }}>
                        Selected: {uploadFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseUploadDialog} color="">
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadSubmit}
                  color="primary"
                  variant="contained"
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        }
      />
    </>
  );
};

export default AgreementTable;

function ShowLoading() {
  return (<div
    id="loading-overlay"
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      zIndex: 9999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  ><img src="../loading.gif" alt="Loading..." style={{ minWidth: "100px", width: "6vw" }} />
  </div>);
}